import { useState, useCallback } from 'react';

/**
 * Ergonomic wrapper around <ConfirmModal>.
 * Returns: { confirm(opts), close, props }
 *
 * Usage:
 *   const confirmModal = useConfirmModal();
 *   const handleDiscard = () => {
 *       confirmModal.confirm({
 *           title: 'Discard draft?',
 *           message: 'This cannot be undone.',
 *           confirmLabel: 'Discard',
 *           variant: 'danger',
 *           onConfirm: async () => { ... },
 *       });
 *   };
 *   ...
 *   <ConfirmModal {...confirmModal.props} />
 */
export default function useConfirmModal() {
    const [state, setState] = useState({
        open: false,
        title: '',
        message: '',
        confirmLabel: 'Confirm',
        cancelLabel: 'Cancel',
        variant: 'default',
        busy: false,
        onConfirm: null,
    });

    const close = useCallback(() => {
        setState((s) => ({ ...s, open: false, busy: false, onConfirm: null }));
    }, []);

    const confirm = useCallback((opts) => {
        setState({
            open: true,
            title: opts.title ?? '',
            message: opts.message ?? '',
            confirmLabel: opts.confirmLabel ?? 'Confirm',
            cancelLabel: opts.cancelLabel ?? 'Cancel',
            variant: opts.variant ?? 'default',
            busy: false,
            onConfirm: opts.onConfirm ?? null,
        });
    }, []);

    const handleConfirm = useCallback(async () => {
        if (!state.onConfirm) { close(); return; }
        setState((s) => ({ ...s, busy: true }));
        try {
            await state.onConfirm();
        } finally {
            close();
        }
    }, [state.onConfirm, close]);

    return {
        confirm,
        close,
        props: {
            open: state.open,
            title: state.title,
            message: state.message,
            confirmLabel: state.confirmLabel,
            cancelLabel: state.cancelLabel,
            variant: state.variant,
            busy: state.busy,
            onConfirm: handleConfirm,
            onCancel: close,
        },
    };
}
